import { useState, type ReactNode } from 'react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { TabBar } from '@/components/ui/TabBar';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { toCsv, downloadCsv } from '@/lib/csv';
import { useTatReport, useTestWiseReport, useLabRevenueReport, useRejectionReport } from './hooks';
import { rejectionLabel, fmtDate } from './ui';
import PatientLabHistory from './PatientLabHistory';
import type { RevenueReportRow } from './types';

/**
 * Laboratory MIS.
 *
 * Every figure is a server-side aggregate over this branch's rows. Two of them
 * are worth reading the query for before trusting a number:
 *
 *   * **Revenue reads `bills`, not the order's own prices.** The order carries
 *     what the tests cost; the bill carries what the hospital actually
 *     invoiced, and a discount or a credit note only moves the second one.
 *     In-patient tests have no invoice of their own — they are charged to the
 *     admission and billed at discharge — so those come from the admission's
 *     charges (read off the discharge bill once there is one) and are shown
 *     separately. Credit notes are netted off, "paid" is what the payment
 *     ledger recorded less refunds, and a cancelled order still counts until
 *     it is credited — cancelling never touches the invoice.
 *   * **TAT measures against `due_at`, snapshotted at order time** — so
 *     retuning a test's turnaround next month does not retroactively make last
 *     month's work look late (or on time).
 */

function exportRows(name: string, rows: Array<Record<string, unknown>>) {
  const first = rows[0];
  if (!first) return;
  const columns = Object.keys(first).map((k) => [k, k] as [string, string]);
  downloadCsv(`${name}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows, columns));
}

type ReportId = 'tat' | 'test-wise' | 'revenue' | 'rejections' | 'patient';

const TABS: Array<{ id: ReportId; label: string }> = [
  { id: 'tat',        label: '⏱ Turnaround' },
  { id: 'test-wise',  label: '🧪 Test-wise' },
  { id: 'revenue',    label: '₹ Revenue' },
  { id: 'rejections', label: '🚫 Rejections' },
  { id: 'patient',    label: '🗂 Patient history' },
];

export default function LabReportsTab() {
  const [tab, setTab] = useState<ReportId>('tat');
  // Default to the last 30 days rather than all time: an unbounded aggregate
  // over a hospital's whole history is slow and answers a question nobody
  // asked.
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  return (
    <div className="space-y-3">
      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab !== 'patient' && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-[12px]">
            <span className="mb-1 block text-muted-foreground">From</span>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="text-[12px]">
            <span className="mb-1 block text-muted-foreground">To</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      )}

      {tab === 'tat' && <TatPanel from={from} to={to} />}
      {tab === 'test-wise' && <TestWisePanel from={from} to={to} />}
      {tab === 'revenue' && <RevenuePanel from={from} to={to} />}
      {tab === 'rejections' && <RejectionsPanel from={from} to={to} />}
      {tab === 'patient' && <PatientHistoryPanel />}
    </div>
  );
}

/**
 * Shared shell: loading/error/empty, plus the CSV export.
 *
 * Columns are derived from the first row's own keys rather than listed per
 * report — these are flat aggregate rows whose column set IS the shape the
 * server returned, so restating it here would be a second list to keep in
 * step with the SQL for no benefit. (lib/csv.ts's toCsv takes explicit
 * columns for the reports where the printed order matters; here it doesn't.)
 */
function Wrap({ query, exportName, rows, children }: {
  query: { isLoading: boolean; error: unknown; refetch: () => void };
  exportName: string;
  rows: Array<Record<string, unknown>>;
  children: ReactNode;
}) {
  if (query.isLoading) return <LoadingState label="Building the report…" />;
  if (query.error) return <ErrorState message={(query.error as Error).message} onRetry={() => query.refetch()} />;
  if (rows.length === 0) return <EmptyState label="Nothing in this range." hint="Widen the dates." />;
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={() => exportRows(exportName, rows)}>Export CSV</Button>
      </div>
      <div className="rounded-xl border border-border bg-card">{children}</div>
    </div>
  );
}

function TatPanel({ from, to }: { from: string; to: string }) {
  const q = useTatReport(from, to);
  const rows = q.data ?? [];
  return (
    <Wrap query={q} exportName="laboratory-tat" rows={rows as unknown as Array<Record<string, unknown>>}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Test</TableHead>
            <TableHead>Department</TableHead>
            <TableHead className="text-right">Reported</TableHead>
            <TableHead className="text-right">Collection → verify</TableHead>
            <TableHead className="text-right">Receipt → verify</TableHead>
            <TableHead className="text-right">Past promised TAT</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.test_code}-${r.department_name}`}>
              <TableCell>{r.test_name} <span className="font-mono text-[11px] text-muted-foreground">{r.test_code}</span></TableCell>
              <TableCell className="text-[12px]">{r.department_name ?? '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{r.reported_count}</TableCell>
              <TableCell className="text-right tabular-nums">
                {r.avg_collection_to_verify_hours ? `${Number(r.avg_collection_to_verify_hours).toFixed(1)} h` : '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {r.avg_receipt_to_verify_hours ? `${Number(r.avg_receipt_to_verify_hours).toFixed(1)} h` : '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {r.breached_count > 0
                  ? <Badge variant="warn">{r.breached_count}</Badge>
                  : <span className="text-muted-foreground">0</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Wrap>
  );
}

function TestWisePanel({ from, to }: { from: string; to: string }) {
  const q = useTestWiseReport(from, to);
  const rows = q.data ?? [];
  return (
    <Wrap query={q} exportName="laboratory-test-wise" rows={rows as unknown as Array<Record<string, unknown>>}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Test</TableHead>
            <TableHead>Department</TableHead>
            <TableHead className="text-right">Ordered</TableHead>
            <TableHead className="text-right">Reported</TableHead>
            <TableHead className="text-right">Cancelled</TableHead>
            <TableHead className="text-right">Abnormal</TableHead>
            <TableHead className="text-right">Critical</TableHead>
            <TableHead className="text-right">Ordered value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.test_code}>
              <TableCell>{r.test_name} <span className="font-mono text-[11px] text-muted-foreground">{r.test_code}</span></TableCell>
              <TableCell className="text-[12px]">{r.department_name ?? '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{r.ordered}</TableCell>
              <TableCell className="text-right tabular-nums">{r.reported}</TableCell>
              <TableCell className="text-right tabular-nums">{r.cancelled}</TableCell>
              <TableCell className="text-right tabular-nums">{r.abnormal}</TableCell>
              <TableCell className="text-right tabular-nums">
                {r.critical > 0 ? <Badge variant="danger">{r.critical}</Badge> : 0}
              </TableCell>
              <TableCell className="text-right tabular-nums">Rs. {Number(r.gross_value ?? 0).toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Wrap>
  );
}

function RevenuePanel({ from, to }: { from: string; to: string }) {
  const q = useLabRevenueReport(from, to);
  const rows = q.data ?? [];
  const sum = (k: keyof RevenueReportRow) => rows.reduce((s, r) => s + Number(r[k] || 0), 0);
  const money = (v: string | number) => `Rs. ${Number(v).toFixed(2)}`;
  const credit = (v: string | number) => (Number(v) > 0 ? `− ${money(v)}` : money(0));
  return (
    <div className="space-y-2">
      <p className="text-[11px] text-muted-foreground">
        Read from the bills, not the ordered test prices. OPD tests use their own invoice; in-patient tests
        are charged to the admission and billed with the stay at discharge; emergency (ED) tests are charged
        to the ED visit and billed on the ER bill — or counted as IP if the patient is admitted. Credit notes are subtracted
        (Credited), Paid is money received less refunds, and a cancelled test still counts until it is
        credited — cancelling a test never changes its bill.
      </p>
      <Wrap query={q} exportName="laboratory-revenue" rows={rows as unknown as Array<Record<string, unknown>>}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Orders</TableHead>
              <TableHead className="text-right">OPD billed</TableHead>
              <TableHead className="text-right">IP orders</TableHead>
              <TableHead className="text-right">IP charged</TableHead>
              <TableHead className="text-right">ED orders</TableHead>
              <TableHead className="text-right">ED charged</TableHead>
              <TableHead className="text-right">Credited</TableHead>
              <TableHead className="text-right">Net billed</TableHead>
              <TableHead className="text-right">Paid</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.day}>
                <TableCell>{fmtDate(r.day)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.orders}
                  {r.cancelled_orders > 0 && (
                    <span className="ml-1 text-[11px] text-muted-foreground">(+{r.cancelled_orders} cancelled)</span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{money(r.opd_billed_amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.ip_orders}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.ip_charged_amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.er_orders ?? 0}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.er_charged_amount ?? 0)}</TableCell>
                <TableCell className="text-right tabular-nums">{credit(r.credited_amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.billed_amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(r.paid_amount)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell />
              <TableCell className="text-right font-semibold tabular-nums">{money(sum('opd_billed_amount'))}</TableCell>
              <TableCell />
              <TableCell className="text-right font-semibold tabular-nums">{money(sum('ip_charged_amount'))}</TableCell>
              <TableCell />
              <TableCell className="text-right font-semibold tabular-nums">{money(sum('er_charged_amount'))}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{credit(sum('credited_amount'))}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{money(sum('billed_amount'))}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{money(sum('paid_amount'))}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Wrap>
    </div>
  );
}

function RejectionsPanel({ from, to }: { from: string; to: string }) {
  const q = useRejectionReport(from, to);
  const rows = q.data ?? [];
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <div className="space-y-2">
      <p className="text-[11px] text-muted-foreground">
        Why samples were rejected, and where they were drawn. A reason trending upward at one collection
        point is a training finding — which is why the reason is a fixed list rather than free text.
      </p>
      <Wrap query={q} exportName="laboratory-rejections" rows={rows as unknown as Array<Record<string, unknown>>}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Count</TableHead>
              <TableHead className="text-right">Share</TableHead>
              <TableHead>Collection points</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.rejection_reason}>
                <TableCell>{rejectionLabel(r.rejection_reason)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.count}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {total ? `${Math.round((r.count / total) * 100)}%` : '—'}
                </TableCell>
                <TableCell className="text-[12px] text-muted-foreground">
                  {(r.locations ?? []).join(', ') || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Wrap>
    </div>
  );
}

function PatientHistoryPanel() {
  const [uhid, setUhid] = useState('');
  const [active, setActive] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => { e.preventDefault(); setActive(uhid.trim() || null); }}
      >
        <label className="text-[12px]">
          <span className="mb-1 block text-muted-foreground">Patient UHID</span>
          <Input value={uhid} onChange={(e) => setUhid(e.target.value)} placeholder="UHID" className="w-[200px]" />
        </label>
        <Button type="submit" size="sm">Show history</Button>
      </form>
      {active && <PatientLabHistory uhid={active} />}
    </div>
  );
}
